<template>
  <div class="wd-flex wd-flex-col wd-gap-3 wd-w-full">
    <!-- 持卡人資訊表單 -->
    <Form ref="cardHolderForm" as="div" class="wd-flex wd-flex-col wd-gap-0">
      <!-- 姓名 -->
      <RemarkFormField
        v-model="formData.name"
        :data-testid="testIds.preview.cardholderNameField"
        :remark="cardholderNameField"
        @update:model-value="handleFieldUpdate('name', $event)"
      />

      <!-- 提示訊息區塊 -->
      <div v-if="showPolicyNotice" class="wd-flex wd-items-start wd-gap-2 wd-px-3 wd-py-2 wd-mb-3 wd-bg-orange10 wd-rounded">
        <div class="wd-flex-shrink-0 wd-w-5 wd-h-5 wd-flex wd-items-center wd-justify-center">
          <funnow-icon class="!wd-min-w-5" max-width="20" image-path="alert-circle-current-color.svg" />
        </div>
        <div class="wd-flex-1 wd-text-sm wd-text-black50">
          <i18n-t keypath="credit_card_verify_info_hint" tag="span">
            <template #rule>
              <span class="wd-cursor-pointer wd-underline" @click="showTapPayFormHint">{{ $t('credit_card_verifty_info_policy_hint') }}</span>
            </template>
          </i18n-t>
        </div>
      </div>

      <!-- 電話號碼 -->
      <RemarkFormField
        v-model="formData.phone"
        :data-testid="testIds.preview.cardholderPhoneField"
        :remark="phoneNumberField"
        @update:model-value="handleFieldUpdate('phone', $event)"
        @option-change="handlePhoneOptionChange"
      />

      <!-- Email -->
      <RemarkFormField
        v-model="formData.email"
        :data-testid="testIds.preview.cardholderEmailField"
        :remark="emailField"
        @update:model-value="handleFieldUpdate('email', $event)"
      />
    </Form>
  </div>
</template>

<script setup lang="ts">
import { type FormSlotProps } from 'vee-validate'
import RemarkFormField from '@/components/RemarkFormField.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import { RemarkInputType, RemarkIdEnum } from '@/view-models/remark'
import { useBaseMessage } from '@/composition/useMessage'
import { useFieldRules } from '@/composables/useFieldRules'
import { useCardholderDefaults } from '@/composables/useCardholderDefaults'
import testIds from '@/constants/test-ids.json'

interface CardholderFormData {
  name: string
  phone: string
  email: string
  phone_country_code?: string
}

defineOptions({
  name: 'CardholderVerificationForm',
})

interface Props {
  showNameField?: boolean
  showPolicyNotice?: boolean

  // 是否為付款連結 (FunBook Prepayment)
  isPaymentLink?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showNameField: false,
  showPolicyNotice: true,
  isPaymentLink: false,
})

const modelValue = defineModel<CardholderFormData>({
  default: () => ({
    name: '',
    phone: '',
    email: '',
  }),
})

const valid = defineModel<boolean>('valid', {
  default: false,
})

const { $i18n, $store } = useNuxtApp()

const cardHolderFormRef = useTemplateRef<FormSlotProps>('cardHolderForm')
const isFormValid = computed(() => {
  return !!cardHolderFormRef.value?.meta.valid
})

syncRefs(isFormValid, valid)

const rules = useFieldRules()

// 取得備註表單資料 (for 電話和 Email 預設值)
const remarkFormData = computed(() => {
  return ($store as any).state.booking?.remarkFormData || {}
})

// 建立 reactive options for useCardholderDefaults
const cardholderOptions = computed(() => ({
  remarkFormData: remarkFormData.value,
  isPaymentLink: props.isPaymentLink,
}))

// 在 root setup 中使用 useCardholderDefaults，並傳入 reactive 的資料
const cardholderDefaults = useCardholderDefaults(cardholderOptions)

// 表單資料
const formData = ref<CardholderFormData>({
  name: '',
  phone: '',
  email: '',
  phone_country_code: 'TW',
})

// 初始化預設值
onMounted(() => {
  initializeDefaults()
})

// 當 cardholderDefaults 的資料變化時，重新初始化預設值
watch(
  () => [cardholderDefaults.defaultName.value, cardholderDefaults.defaultPhone.value, cardholderDefaults.defaultEmail.value],
  () => {
    initializeDefaults()
  },
  { deep: true }
)

function initializeDefaults() {
  // 設定姓名預設值
  formData.value.name = cardholderDefaults.defaultName.value

  // 設定電話預設值
  const defaultPhone = cardholderDefaults.defaultPhone.value
  formData.value.phone = defaultPhone.phone
  formData.value.phone_country_code = defaultPhone.countryCode

  // 設定 Email 預設值
  formData.value.email = cardholderDefaults.defaultEmail.value

  modelValue.value = { ...formData.value }

  // 如果表單有效，立即發送更新
  if (isFormValid.value) {
    emitCardholderData()
  }
}

// 建立姓名欄位設定
const cardholderNameField = computed(() => ({
  id: 'cardholder_name',
  name: 'cardholder_name',
  type: RemarkInputType.Text,
  title: props.showNameField ? $i18n.t('credit_card_english_name') : '',
  subtitle: '',
  content: '',
  placeholder: $i18n.t('credit_card_english_name_hint'),
  is_required: true,
  opt: [],
  opt_default_idx: null,
  last_input: null,
  error_msg: $i18n.t('credit_card_field_english_name_hint'),
  ruleOption: {
    required: true,
    schema: rules.cardholderName($i18n.t('credit_card_field_english_name_hint')),
  },
}))

// 建立電話號碼欄位設定
// option 順序：台灣 > 香港 > 日本 > 馬來西亞 > 泰國
const regionPriority = ['TW', 'HK', 'JP', 'MY', 'TH']
const phoneRegionList = phoneHelpers.getSupportedRegions().sort((a, b) => {
  const idxA = regionPriority.indexOf(a)
  const idxB = regionPriority.indexOf(b)
  if (idxA === -1 && idxB === -1) return 0
  if (idxA === -1) return 1
  if (idxB === -1) return -1
  return idxA - idxB
})
const phoneNumberField = computed(() => {
  // 根據 formData 中的 phone_country_code 來設定預設選項
  const defaultCountryCode = formData.value.phone_country_code || 'TW'
  const defaultIndex = phoneRegionList.findIndex(region => region === defaultCountryCode)

  return {
    id: 'phone_number',
    name: 'phone_number',
    type: RemarkInputType.NationalPhoneNumber,
    title: $i18n.t('credit_card_phone'),
    subtitle: '',
    content: '',
    placeholder: '',
    is_required: true,
    opt: phoneRegionList.map((regionName, index) => ({ id: index + 1, value: regionName, country_id: index + 1 })),
    opt_default_idx: defaultIndex >= 0 ? defaultIndex : phoneRegionList.findIndex(region => region === 'TW'),
    last_input: null,
    error_msg: $i18n.t('credit_card_field_phone_hint'),
    ruleOption: {
      required: true,
    },
  }
})

// 建立 Email 欄位設定
const emailField = computed(() => ({
  id: RemarkIdEnum.EMAIL,
  name: 'email',
  type: RemarkInputType.Text,
  title: $i18n.t('credit_card_email'),
  subtitle: '',
  content: '',
  placeholder: $i18n.t('credit_card_field_email_placeholder'),
  is_required: true,
  opt: [],
  opt_default_idx: null,
  last_input: null,
  error_msg: $i18n.t('credit_card_field_email_hint'),
  ruleOption: {
    required: true,
  },
}))

const { showMessageModal } = useBaseMessage({
  data: {
    header: ref($i18n.t('credit_card_verify_info_hint_modal_title')),
    message: ref($i18n.t('credit_card_verify_info_hint_modal_content')),
    useCloseIcon: ref(true),
  },
})

// 處理欄位更新
function handleFieldUpdate(fieldName: keyof CardholderFormData, value: string) {
  formData.value[fieldName] = value.trim()
  if (isFormValid.value) {
    emitCardholderData()
  }
}

// 處理電話國碼選擇
function handlePhoneOptionChange([remarkId, optionId]: [number, number]) {
  const selectedOption = phoneNumberField.value.opt.find(opt => opt.id === optionId)
  if (selectedOption) {
    formData.value.phone_country_code = selectedOption.value
    if (isFormValid.value) {
      emitCardholderData()
    }
  }
}

const emitCardholderData = useDebounceFn(() => {
  if (!isFormValid.value) return
  const { name, phone, email } = formData.value
  modelValue.value = { name, phone, email }
}, 100)

function showTapPayFormHint() {
  showMessageModal()
}
</script>
